/**
 * PointSelectionHandler
 * POINT Entity 타입의 선택 핸들러 로직
 */

import type { SelectionStrategy } from '../../base/selectionStrategy'
import type { BaseSelectionHandler } from '../../base/baseSelectionHandler'
import { CANVAS_ENTITY_EVENT } from '../../../const'

export class PointSelectionHandler implements SelectionStrategy {
  /**
   * POINT 타입 선택 핸들러 추가
   * @param entity - BaseSelectionHandler 인스턴스
   */
  addHandlers(entity: BaseSelectionHandler): void {
    const systemLayer = entity.canvasEngine.getRenderer().layers.system
    if (!systemLayer) {
      throw new Error('시스템 레이어가 없습니다.')
    }

    const shape = entity.getShape()
    if (!shape) {
      throw new Error('도형이 없습니다.')
    }

    // Transformer 초기화
    entity.destroyTransformer()
    const target = shape

    const interactionEnabled =
      entity.getEntityInteractionMode() && entity.isSelected && !entity.canvasEngine.isPinchZooming

    // 드래그 가능하게 설정
    target.draggable(interactionEnabled)

    // hit stroke 끄기 — 원본은 hitStrokeWidth(parseInt(hitStrokeWidth()))로 'auto'를 NaN으로 만들었고
    // Konva hasHitStroke()는 NaN·0을 같이 false로 본다(점 hitFunc 원의 테두리 stroke만 hit에서 빠짐)
    target.hitArea({ kind: 'strokeWidth', px: 0 })

    // Transformer 생성 (POINT는 회전 비활성화)
    const shapeTransformer = entity.createSelectionHandles(
      {
        id: 'single-selection',
        padding: 20,
        anchorCornerRadius: 0,
        anchorSize: 0,
        rotateEnabled: false, // POINT는 회전 불가
        resizeEnabled: false,
        borderStrokeWidth: 2,
        ignoreStroke: true,
        borderHitStrokeWidth: entity.TRANSFORMER_HIT_STROKE_WIDTH,
        draggable: interactionEnabled,
        anchorDraggable: interactionEnabled,
        anchorHitStrokeWidth: entity.TRANSFORMER_HIT_STROKE_WIDTH,
      },
      [target],
    )
    entity.setShapeTransformer(shapeTransformer)
    entity.canvasEngine.initShapeTransformer(shapeTransformer)

    // Transform Start 이벤트
    const eventManager = entity.getEventManager()
    eventManager.removeEvent(target, CANVAS_ENTITY_EVENT.SELECT_TRANSFORM_START)
    eventManager.addEvent(target, CANVAS_ENTITY_EVENT.SELECT_TRANSFORM_START, () => {
      if (!entity.isSelected) return
      if (entity.canvasEngine.isPinchZooming) return
      entity.removeShapeAnimation()
    })

    // Transform End 이벤트
    eventManager.removeEvent(target, CANVAS_ENTITY_EVENT.SELECT_TRANSFORM_END)
    eventManager.addEvent(target, CANVAS_ENTITY_EVENT.SELECT_TRANSFORM_END, () => {
      if (!entity.isSelected) return
      if (entity.canvasEngine.isPinchZooming) return

      entity.transformShape()
      entity.canvasEngine.restoreAllEntitiyListening()
      entity.addShapeAnimation()
      entity.updateBalloonPositionPublic()
      entity.canvasEngine.events.onEntityTransform(entity.getId())
      entity.showButtonGroup()
      entity.updateButtonPosition()
    })

    // Drag Start 이벤트
    eventManager.removeEvent(target, CANVAS_ENTITY_EVENT.SELECT_DRAG_START)
    eventManager.addEvent(target, CANVAS_ENTITY_EVENT.SELECT_DRAG_START, () => {
      if (!entity.isSelected) return
      if (entity.canvasEngine.isPinchZooming) {
        // 멀티터치 중이면 드래그 중단
        if (target.isDragging()) {
          target.stopDrag()
        }
        return
      }

      // 파동 애니메이션 모드에 따른 처리
      if (entity.balloon) {
        if (!entity.getRippleFollowMode()) {
          entity.removeShapeAnimation()
        }
      }
    })

    // Drag Move 이벤트
    eventManager.removeEvent(target, CANVAS_ENTITY_EVENT.SELECT_DRAG_MOVE)
    eventManager.addEvent(target, CANVAS_ENTITY_EVENT.SELECT_DRAG_MOVE, () => {
      if (!entity.isSelected) return
      if (entity.canvasEngine.isPinchZooming) return

      // Shape의 드래그된 위치를 Entity 위치로 실시간 반영 (Rect와 동일한 방식)
      const x = target.x()
      const y = target.y()
      entity.setShapePosition(0, 0)
      entity.setEntityPosition(entity.node.x() + x, entity.node.y() + y)

      // 파동 애니메이션 따라다니기 모드
      if (entity.balloon && entity.getRippleFollowMode()) {
        entity.updateRipplePosition()
      }

      entity.hideButtonGroup()
    })

    // Drag End 이벤트
    eventManager.removeEvent(target, CANVAS_ENTITY_EVENT.SELECT_DRAG_END)
    eventManager.addEvent(target, CANVAS_ENTITY_EVENT.SELECT_DRAG_END, () => {
      if (!entity.isSelected) return
      if (entity.canvasEngine.isPinchZooming) return

      // 최종 위치 반영 (Rect와 동일한 방식)
      const x = target.x()
      const y = target.y()
      entity.setShapePosition(0, 0)
      entity.setEntityPosition(entity.node.x() + x, entity.node.y() + y)

      // 파동 애니메이션 복원
      if (entity.balloon && !entity.getRippleFollowMode()) {
        entity.addShapeAnimation()
      }

      entity.updateBalloonPositionPublic()
      entity.canvasEngine.events.onEntityTransform(entity.getId())
      entity.showButtonGroup()
      entity.updateButtonPosition()
    })

    // 시스템 레이어에 Transformer 추가
    systemLayer.add(shapeTransformer.node)
    systemLayer.batchDraw()
  }
}
